
import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@ellucian/react-design-system/core';
import { Icon } from '@ellucian/ds-icons/lib';
import { Button } from '@ellucian/react-design-system/core';
import { useNavigate } from 'react-router-dom';
import { NavigationHeaderBar } from '../components/Navigation/NavigationHeaderBar';
import FaqAccordion from '../components/FAQ/FaqAccordion';

const generalFaqs = [
  {
    question: 'What is the Marketplace and how does it work?',
    answer: 'The Marketplace is a curated space where you can discover solutions designed to enhance your platform. Each listing includes details about what the solution does, its benefits, and any requirements for implementation. When you find a solution that fits your needs, you can follow the provided steps to get started or request more information from the provider.'
  },
  {
    question: 'Are there any costs associated with using Marketplace offerings?',
    answer: 'Some solutions in the Marketplace are free, while others may require a purchase, subscription, or licensing fee. Pricing and cost details are provided on each solution’s listing page. Please review the solution details or contact the provider for specific pricing information.'
  },
  {
    question: 'How do I find the right solution for my institution’s needs?',
    answer: 'You can use the search and filter features to narrow down solutions by category, type, integration, or provider. Each solution page includes a detailed description, features, and requirements to help you determine if it fits your needs. If you need further assistance, you can contact our support team or the solution provider directly.'
  }
];

const technicalFaqs = [
  {
    question: 'How do I enable a solution from the Marketplace?',
    answer: 'To enable a solution, navigate to its listing page and follow the instructions provided. This may include requesting access, installing an integration, or contacting the provider. Some solutions may require administrative approval or additional configuration.'
  },
  {
    question: 'What should I do if something isn’t working properly?',
    answer: 'If you encounter issues with a solution, first review the troubleshooting steps or documentation provided on the solution’s page. If the problem persists, contact the solution provider using the contact information listed, or reach out to our support team for further assistance.'
  },
  {
    question: 'Where can I find the system requirements?',
    answer: 'System requirements for each solution are listed on its Marketplace page, typically under a “Requirements” or “Technical Details” section. Please review these details before enabling a solution to ensure compatibility with your environment.'
  }
];

const accountFaqs = [
  {
    question: 'Do I need a separate account to access the Marketplace?',
    answer: 'You can access the Marketplace using your existing platform credentials. In some cases, additional permissions may be required to request or enable solutions. If you are unable to access certain features, please contact your administrator.'
  },
  {
    question: 'How do I update my organization’s details?',
    answer: 'Organization details can typically be updated by an administrator in your account settings or profile section. If you need assistance updating your information, please contact your organization’s administrator or our support team.'
  },
  {
    question: 'Who in my team can manage and approve requests?',
    answer: 'Only users with the appropriate administrative permissions can manage and approve solution requests. If you are unsure who has these permissions, please check with your organization’s administrator or review your account roles in the settings.'
  }
];

/**
 * FAQPage
 *
 * Displays categorized FAQs with expand/collapse functionality.
 * Uses state to track which question is open. Integrates with navigation and responsive layout.
 */
const FAQPage: React.FC = () => {
  // Tracks which FAQ (by section and index) is currently expanded
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const navigate = useNavigate();

  const toggleIndex = (key: string) => setOpenIndex(prev => (prev === key ? null : key));

  // Scroll to top on mount (when navigating to FAQ page)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', background: '#fff' }}>
      {/* Shared Navigation Header (sticky, always visible) - non-landmark wrapper so inner header is the banner landmark */}
      <div
        role="presentation"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'var(--Primary-iris-600, #7100EB)',
          transition: 'background 0.3s ease',
        }}
      >
        <NavigationHeaderBar isAtTop={false} />
      </div>
      {/* Main landmark: FAQ Hero Section and FAQ content */}
      <main>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '8px',
          background: 'var(--Interactive-Iris-cta-iris-tint, #F5EEFB)',
          color: '#151618',
          borderRadius: 0,
          boxShadow: '0 2px 8px 0 rgba(113,0,235,0.08)',
          mx: 'auto',
          px: { xs: 2, sm: 4, md: 8 },
          pt: { xs: 4, sm: 5, md: 6 },
          pb: { xs: 6, sm: 8, md: 9 },
        }}
      >
        {/* Back button left-aligned */}
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Button
            variant="text"
            onClick={() => navigate(-1)}
            startIcon={<Icon name="chevron-left" style={{ width: 16, height: 16, color: '#0056D2' }} />}
            sx={{
              display: 'flex',
              padding: { xs: '8px 0 8px 0', sm: '8px 16px 8px 0' },
              justifyContent: 'flex-start',
              alignItems: 'center',
              borderRadius: '4px',
              fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              fontSize: { xs: 14, sm: 16 },
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.48px',
              color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
              background: 'var(--Interactive-Iris-cta-iris-tint, #F5EEFB) !important',
              minWidth: 0,
              boxShadow: 'none',
              outline: 'none',
              marginLeft: 0,
              mr: 2,
              textTransform: 'none',
              '&:hover': {
                background: 'var(--Interactive-Iris-cta-iris-tint, #F5EEFB) !important',
                color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                boxShadow: 'none',
              },
              '&:focus': {
                outline: 'none',
                boxShadow: 'none',
                background: 'var(--Interactive-Iris-cta-iris-tint, #F5EEFB) !important',
                color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
              },
              '&:active': {
                outline: 'none',
                boxShadow: 'none',
                background: 'var(--Interactive-Iris-cta-iris-tint, #F5EEFB) !important',
                color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
              },
            }}
          >
          Back
          </Button>
        </Box>
        {/* Centered FAQ title and description */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              color: 'var(--Neutral-neutral-600, #151618)',
              textAlign: 'center',
              fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              fontSize: '40px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '44px',
              letterSpacing: '1.2px',
              mt: 0.5,
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'var(--Neutral-neutral-600, #151618)',
              textAlign: 'center',
              alignSelf: 'stretch',
              fontFamily: '"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '20px',
              letterSpacing: '0.08px',
              mt: 1,
            }}
          >
            Find answers to common questions about our services, process, and policies.
          </Typography>
        </Box>
      </Box>
      {/* FAQ Sections: General, Technical, Account */}
      <Box sx={{
        maxWidth: { xs: '100%', sm: 700, md: 1000, lg: 1200 },
        width: '100%',
        mx: 'auto',
        mt: { xs: 3, sm: 6 },
        mb: { xs: 4, sm: 8 },
        px: { xs: 1, sm: 3, md: 4, lg: 6 },
        pb: { xs: 2, sm: 4 },
        maxHeight: 'calc(100vh - 240px)',
        overflowY: 'auto',
        pr: 2
      }}>
        {/* General Inquiries Section */}
  <Box
    tabIndex={0}
    onKeyDown={(e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const el = document.getElementById('general-0-label') as HTMLElement | null;
        if (el) el.focus();
      }
    }}
    sx={{ outline: 'none' }}
  >
    <Typography
      variant="h2"
      sx={{
        color: 'var(--Neutral-neutral-600, #151618)',
        fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        fontSize: '24px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '26.4px',
        mt: '32px',
        mb: 1,
      }}
    >
      General Inquiries
    </Typography>
  </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'var(--Neutral-neutral-500, #5B5E65)',
            fontFamily: '"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '17.5px',
            letterSpacing: '0.07px',
            mb: { xs: 5, sm: 6 },
          }}
        >
          Learn about Marketplace basics, including availability, pricing, and how it benefits your institution.
        </Typography>
        <Box>
          <FaqAccordion section="general" items={generalFaqs} openKey={openIndex} onToggle={toggleIndex} />
        </Box>
        
        {/* Technical Support Section */}
  <Box
    tabIndex={0}
    onKeyDown={(e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const el = document.getElementById('tech-0-label') as HTMLElement | null;
        if (el) el.focus();
      }
    }}
    sx={{ outline: 'none' }}
  >
    <Typography
      variant="h2"
      sx={{
        color: 'var(--Neutral-neutral-600, #151618)',
        fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        fontSize: '24px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '26.4px',
        mt: 6,
        mb: 1,
      }}
    >
      Technical Support
    </Typography>
  </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'var(--Neutral-neutral-500, #5B5E65)',
            fontFamily: '"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '17.5px',
            letterSpacing: '0.07px',
            mb: { xs: 5, sm: 6 },
          }}
        >
          Get help with troubleshooting issues, integrations, and technical requirements.
        </Typography>
        <Box>
          <FaqAccordion section="tech" items={technicalFaqs} openKey={openIndex} onToggle={toggleIndex} />
        </Box>
        
        {/* Account Management Section */}
  <Box
    tabIndex={0}
    onKeyDown={(e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const el = document.getElementById('account-0-label') as HTMLElement | null;
        if (el) el.focus();
      }
    }}
    sx={{ outline: 'none' }}
  >
    <Typography
      variant="h2"
      sx={{
        color: 'var(--Neutral-neutral-600, #151618)',
        fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        fontSize: '24px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '26.4px',
        mt: 6,
        mb: 1,
      }}
    >
      Account Management
    </Typography>
  </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'var(--Neutral-neutral-500, #5B5E65)',
            fontFamily: '"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '17.5px',
            letterSpacing: '0.07px',
            mb: { xs: 5, sm: 6 },
          }}
        >
          Learn how to create, update, and manage your Marketplace account and settings.
        </Typography>
        <Box>
          <FaqAccordion section="account" items={accountFaqs} openKey={openIndex} onToggle={toggleIndex} />
        </Box>
      </Box>
      </main>
    </Box>
  );
};

export default FAQPage;